import { BaseApiService } from './base';

export interface ContentProject {
  id: string;
  name: string;
  type: 'visual' | 'presentation' | 'poster' | 'web' | 'internal';
  status: 'draft' | 'in_progress' | 'review' | 'completed';
  description: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  collaborators: string[];
  assets: ContentAsset[];
  settings: ContentSettings;
}

export interface ContentAsset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document' | 'template';
  url: string;
  size: number;
  createdAt: Date;
}

export interface ContentSettings {
  theme: string;
  colors: string[];
  fonts: string[];
  layout: string;
  branding: {
    logo?: string;
    colors: string[];
    fonts: string[];
  };
}

export interface DesignTemplate {
  id: string;
  name: string;
  category: 'presentation' | 'poster' | 'social' | 'document';
  thumbnail: string;
  preview: string;
  tags: string[];
  isPremium: boolean;
}

export interface StoryTemplate {
  id: string;
  name: string;
  type: 'narrative' | 'persuasive' | 'informative' | 'emotional';
  structure: string[];
  examples: string[];
  tips: string[];
}

export class ContentService extends BaseApiService {
  // Gestion des projets de contenu
  async getContentProjects(): Promise<ContentProject[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [
      {
        id: '1',
        name: 'Présentation Q1 2024',
        type: 'presentation',
        status: 'in_progress',
        description: 'Présentation des résultats du premier trimestre',
        tags: ['business', 'quarterly', 'results'],
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-01-20'),
        createdBy: 'user1',
        collaborators: ['user2', 'user3'],
        assets: [],
        settings: {
          theme: 'corporate',
          colors: ['#3B82F6', '#1F2937', '#F59E0B'],
          fonts: ['Inter', 'Roboto'],
          layout: 'modern',
          branding: {
            colors: ['#3B82F6', '#1F2937'],
            fonts: ['Inter']
          }
        }
      },
      {
        id: '2',
        name: 'Affiche Événement Marketing',
        type: 'poster',
        status: 'draft',
        description: 'Affiche pour l\'événement marketing de mars',
        tags: ['marketing', 'event', 'poster'],
        createdAt: new Date('2024-01-18'),
        updatedAt: new Date('2024-01-18'),
        createdBy: 'user1',
        collaborators: ['user4'],
        assets: [],
        settings: {
          theme: 'creative',
          colors: ['#EC4899', '#8B5CF6', '#10B981'],
          fonts: ['Poppins', 'Montserrat'],
          layout: 'dynamic',
          branding: {
            colors: ['#EC4899', '#8B5CF6'],
            fonts: ['Poppins']
          }
        }
      }
    ];
  }

  async createContentProject(projectData: Partial<ContentProject>): Promise<ContentProject> {
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const newProject: ContentProject = {
      id: Date.now().toString(),
      name: projectData.name || 'Nouveau projet',
      type: projectData.type || 'visual',
      status: 'draft',
      description: projectData.description || '',
      tags: projectData.tags || [],
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'user1',
      collaborators: projectData.collaborators || [],
      assets: [],
      settings: {
        theme: 'modern',
        colors: ['#3B82F6', '#1F2937', '#F59E0B'],
        fonts: ['Inter', 'Roboto'],
        layout: 'clean',
        branding: {
          colors: ['#3B82F6', '#1F2937'],
          fonts: ['Inter']
        }
      }
    };
    
    return newProject;
  }

  async updateContentProject(id: string, projectData: Partial<ContentProject>): Promise<ContentProject> {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    return {
      id,
      name: projectData.name || 'Projet mis à jour',
      type: projectData.type || 'visual',
      status: projectData.status || 'draft',
      description: projectData.description || '',
      tags: projectData.tags || [],
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'user1',
      collaborators: projectData.collaborators || [],
      assets: projectData.assets || [],
      settings: projectData.settings || {
        theme: 'modern',
        colors: ['#3B82F6', '#1F2937', '#F59E0B'],
        fonts: ['Inter', 'Roboto'],
        layout: 'clean',
        branding: {
          colors: ['#3B82F6', '#1F2937'],
          fonts: ['Inter']
        }
      }
    };
  }

  async deleteContentProject(id: string): Promise<{ success: boolean }> {
    await new Promise(resolve => setTimeout(resolve, 400));
    return { success: true };
  }

  // Templates de design
  async getDesignTemplates(category?: string): Promise<DesignTemplate[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const templates: DesignTemplate[] = [
      {
        id: '1',
        name: 'Présentation Moderne',
        category: 'presentation',
        thumbnail: '/templates/presentation-modern.jpg',
        preview: '/templates/presentation-modern-preview.jpg',
        tags: ['business', 'modern', 'clean'],
        isPremium: false
      },
      {
        id: '2',
        name: 'Affiche Événement',
        category: 'poster',
        thumbnail: '/templates/poster-event.jpg',
        preview: '/templates/poster-event-preview.jpg',
        tags: ['event', 'dynamic', 'colorful'],
        isPremium: false
      },
      {
        id: '3',
        name: 'Post Social Media',
        category: 'social',
        thumbnail: '/templates/social-post.jpg',
        preview: '/templates/social-post-preview.jpg',
        tags: ['social', 'engagement', 'viral'],
        isPremium: true
      },
      {
        id: '4',
        name: 'Rapport Professionnel',
        category: 'document',
        thumbnail: '/templates/document-report.jpg',
        preview: '/templates/document-report-preview.jpg',
        tags: ['professional', 'formal', 'business'],
        isPremium: false
      }
    ];
    
    if (category) {
      return templates.filter(t => t.category === category);
    }
    
    return templates;
  }

  // Templates de storytelling
  async getStoryTemplates(type?: string): Promise<StoryTemplate[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const templates: StoryTemplate[] = [
      {
        id: '1',
        name: 'Histoire de Transformation',
        type: 'narrative',
        structure: ['Introduction', 'Défi', 'Solution', 'Résultat', 'Conclusion'],
        examples: [
          'Avant : Problème complexe',
          'Pendant : Processus de résolution',
          'Après : Succès obtenu'
        ],
        tips: [
          'Commencez par un hook accrocheur',
          'Utilisez des données concrètes',
          'Terminez par un appel à l\'action'
        ]
      },
      {
        id: '2',
        name: 'Argumentaire Persuasif',
        type: 'persuasive',
        structure: ['Problème', 'Agitation', 'Solution', 'Preuve', 'Action'],
        examples: [
          'Identifiez le problème principal',
          'Amplifiez les conséquences',
          'Présentez votre solution unique'
        ],
        tips: [
          'Utilisez des statistiques impactantes',
          'Créez un sentiment d\'urgence',
          'Proposez une solution claire'
        ]
      },
      {
        id: '3',
        name: 'Guide Informatif',
        type: 'informative',
        structure: ['Contexte', 'Explication', 'Exemples', 'Applications', 'Résumé'],
        examples: [
          'Définissez le concept clairement',
          'Donnez des exemples concrets',
          'Montrez les applications pratiques'
        ],
        tips: [
          'Utilisez un langage simple',
          'Incluez des visuels explicatifs',
          'Structurez logiquement l\'information'
        ]
      }
    ];
    
    if (type) {
      return templates.filter(t => t.type === type);
    }
    
    return templates;
  }

  // Outils de design
  async generateVisual(prompt: string, settings: any): Promise<{ url: string; metadata: any }> {
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    return {
      url: '/generated/visual-' + Date.now() + '.jpg',
      metadata: {
        prompt,
        settings,
        generatedAt: new Date(),
        model: 'AI-Design-Generator'
      }
    };
  }

  async createPresentation(slides: any[], template: string): Promise<{ url: string; slides: number }> {
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      url: '/presentations/presentation-' + Date.now() + '.pptx',
      slides: slides.length
    };
  }

  async generateContent(prompt: string, type: 'web' | 'internal' | 'social'): Promise<string> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const contentTemplates = {
      web: `# ${prompt}

## Introduction
Contenu web optimisé pour le référencement et l'engagement utilisateur.

## Points clés
- Point important 1
- Point important 2
- Point important 3

## Conclusion
Appel à l'action et résumé des points essentiels.`,
      
      internal: `# ${prompt}

## Résumé exécutif
Document interne pour la communication d'organisation.

## Détails
Informations détaillées et analyses internes.

## Actions requises
- Action 1
- Action 2
- Action 3`,
      
      social: `🎯 ${prompt}

💡 Point clé à retenir
📊 Données intéressantes
🚀 Appel à l'engagement

#contenu #engagement #socialmedia`
    };
    
    return contentTemplates[type];
  }

  // Statistiques et analytics
  async getContentStats(): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    return {
      totalProjects: 12,
      activeProjects: 8,
      completedProjects: 4,
      templatesUsed: 25,
      contentGenerated: 156,
      averageEngagement: 78
    };
  }

  async getContentAnalytics(projectId: string): Promise<any> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    return {
      views: 1250,
      engagement: 78,
      shares: 45,
      conversions: 12,
      timeSpent: '2m 30s',
      topPerformingContent: 'Slide 3 - Résultats Q1'
    };
  }
}

// Instance singleton
export const contentService = new ContentService();
